import React, { createContext, useContext, useEffect, useState } from 'react';

import { Root, Portal, Overlay, Content } from '@radix-ui/react-dialog';
import Button from './button.jsx';

/* ========================================================================= */
/* Context */
/* ========================================================================= */

const DialogContext = createContext(null);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * Close button that goes through the Dialog's animated close path,
 * keeping the overlay on screen during the exit animation and preventing
 * touch-passthrough to elements underneath (e.g. the site header).
 *
 * Must be rendered as a descendant of a Dialog component.
 */
export const DialogCloseButton = ({ children, ...props }) => {
    const handleClose = useContext(DialogContext);
    const handleTouchEnd = (e) => {
        e.preventDefault(); // prevent iOS from synthesising a ghost click after touch
        handleClose();
    };
    return (
        <Button
            onClick={handleClose}
            onTouchEnd={handleTouchEnd}
            {...props}>
            {children}
        </Button>
    );
};

/**
 * @type {import('$types/components/partials/ui.d.ts').Dialog}
 */
export const Dialog = ({
    animationDuration = 300,
    children,
    className,
    onOpenChange,
    open = false,
    warnBeforeClose,
}) => {
    const [animationOpen, setAnimationOpen] = useState(open);

    useEffect(() => {
        setAnimationOpen(open);
    }, [open]);

    const handleOpenChange = (open = false) => {
        setAnimationOpen(open);
        if (open) {
            onOpenChange(open);
        } else {
            setTimeout(() => onOpenChange(open), animationDuration);
        }
    };

    const handleWarning = (e) => {
        e?.preventDefault();
        if (warnBeforeClose) {
            if (window.confirm(warnBeforeClose)) {
                handleOpenChange(false);
            }
        } else {
            handleOpenChange(false);
        }
    };

    return (
        <DialogContext.Provider value={handleWarning}>
            <Root
                modal={true}
                onOpenChange={handleOpenChange}
                open={open}>
                <Portal>
                    <Overlay className={`dialog__overlay${className ? ` ${className}` : ''} ${animationOpen ? 'dialog__overlay--open' : 'dialog__overlay--closed'}`}>
                        <Content
                            className='dialog__content'
                            onEscapeKeyDown={warnBeforeClose ? handleWarning : undefined}
                            onPointerDownOutside={warnBeforeClose ? handleWarning : undefined}>
                            {children}
                        </Content>
                    </Overlay>
                </Portal>
            </Root>
        </DialogContext.Provider>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default Dialog;
